import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useEffect, useId, useState } from 'react'
import * as styles from './disclosure.css.ts'

export type DisclosureProps = React.PropsWithChildren<{
  /** The text of the summary row the user clicks to expand. */
  summary: string
  /**
   * Whether the section starts expanded. Raising this from `false` to `true` after mount also
   * expands it: a caller sets it when a stored value or a server error turns out to be inside the
   * section, and the second of those is only known once the save has been answered. The user can
   * still collapse it again.
   */
  defaultOpen?: boolean
  /**
   * Holds the section open and takes the toggle out of service. For content the user must not be
   * able to hide — a validation error inside the section that is disabling the form's submit button,
   * where collapsing it would leave a disabled button with nothing on screen explaining it.
   */
  isForcedOpen?: boolean
  className?: string
}>

/**
 * A collapsible group of fields. Rendered as a `fieldset` so the fields inside it are grouped for
 * assistive technology whether or not the section is open, with the toggle in the `legend` so the
 * group's accessible name is the same string the user clicked.
 */
export const Disclosure: React.FC<DisclosureProps> = ({
  summary,
  defaultOpen = false,
  isForcedOpen = false,
  className,
  children,
}) => {
  const contentId = useId()
  const [isOpen, setIsOpen] = useState(defaultOpen)

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true)
    }
  }, [defaultOpen])

  const isExpanded = isOpen || isForcedOpen

  return (
    <fieldset className={classNames(styles.disclosure, className)}>
      <legend className={styles.legend}>
        <button
          type="button"
          className={styles.summary}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          // `aria-disabled` rather than `disabled`, which would take the toggle out of the tab order:
          // a keyboard user tabbing the form would then pass straight over the one control that says
          // this section is being held open. The click is ignored instead, which is all `disabled`
          // was here to do.
          aria-disabled={isForcedOpen ? true : undefined}
          onClick={() => (isForcedOpen ? undefined : setIsOpen(open => !open))}
        >
          {isExpanded ? (
            <CaretDownIcon size={16} weight="regular" className={styles.caret} />
          ) : (
            <CaretRightIcon size={16} weight="regular" className={styles.caret} />
          )}
          {summary}
        </button>
      </legend>
      <div id={contentId} className={styles.content} hidden={!isExpanded}>
        {children}
      </div>
    </fieldset>
  )
}
